import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  StoreHighlightView,
  StorePolicyView,
  StoreView,
} from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateSellerStore } from "@/lib/query/core/invalidation";

type UpdateSellerStoreInput = {
  storeSlug: string;
} & Partial<
  Pick<StoreView, "storeName" | "description" | "descriptionEn"> & {
    /**
     * Object key for the store branding logo. The server renders this into a
     * full URL on read (the wire-out field stays `logoUrl`). Set to `""` to
     * clear the existing logo.
     */
    logoObjectKey: string;
    highlights: Array<Pick<StoreHighlightView, "textAr" | "textEn">>;
    policies: Array<
      Pick<StorePolicyView, "policyType" | "isEnabled" | "contentAr" | "contentEn"> & {
        sortOrder?: number;
      }
    >;
  }
>;

export function useUpdateStoreMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: UpdateSellerStoreInput) => {
      const { storeSlug, ...body } = input;
      void storeSlug;
      return apiJsonClient<StoreView>(endpoints.seller.store, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    onSuccess: async (_data, variables) => {
      await invalidateSellerStore(queryClient, variables.storeSlug);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
